import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import { useTranslation } from '@fluxora/i18n';
import { Alert, Button, Card, CardBody } from '@fluxora/ui';

import { buildStatusPath } from '../services/sign-completion.service';
import { AgreementDownloadWhenReady } from './AgreementDownloadWhenReady';

/**
 * ── "YOU'RE ENROLLED" — THE END OF THE FUNNEL ──────────────────────────────
 *
 * The completion state of the sign step: the contract is executed, and this is
 * everything the customer needs at that moment.
 *
 * ── THE DEFECT THIS REPLACES (measured 2026-08-26) ─────────────────────────
 * The confirmation was an `Alert` that appeared in place, ~25 s after the click,
 * with no focus change, no announcement and no scroll handling — on a page that
 * collapsed from ~3,100px to under a viewport at the same instant. The reporter's
 * words were that the SPA "did not navigate, showed no confirmation, and surfaced
 * no error… it sat on the sign page looking unfinished". Worse, it was purely an
 * artefact of the in-memory mutation result: REFRESHING the same URL after signing
 * re-rendered the whole sign surface, including a live "Sign and enroll" button,
 * in front of a customer whose contract was already executed.
 *
 * Three things fix that, and all three are here:
 *
 *   1. IT IS ANNOUNCED AND FOCUSED. The heading is programmatically focusable and
 *      receives focus on mount, so a screen reader reads the outcome and a sighted
 *      customer's viewport is taken to it regardless of where they were scrolled.
 *      `role="status"` on the wrapper announces it without stealing the reading
 *      position of anyone already navigating.
 *   2. IT CARRIES THE ROUTE TO THE CONTRACT. The signed PDF download AND a link to
 *      the anonymous status page, so the customer leaves with somewhere to go.
 *   3. IT IS RE-DERIVABLE. The caller resolves it from `GET /bff/funnel`
 *      (`signed` + `statusToken`), not from the mutation — see
 *      `sign-completion.service.resolveSignStepView`.
 *
 * ── WHY THIS IS NOT A `<Navigate>` TO /status/:token ───────────────────────
 * That was the obvious shape and it is worse. The status page has no contract
 * download at all, and a just-signed enrollment resolves there as `PENDING`, which
 * renders neither the service start date nor the rescission deadline. Redirecting
 * would therefore have shown the customer strictly LESS at the moment they most
 * need it — including dropping a §25.478/rescission date that is regulatory, not
 * decorative. The status page is offered as a link instead, which is the thing the
 * customer actually needs from it: a durable address for the enrollment.
 */
export interface SignCompletionPanelProps {
  /** The anonymous, unguessable handle for this enrollment's status page. */
  readonly statusToken: string;
  /**
   * The locked-in service start date, when this tab is the one that signed.
   * `null` on a re-derived completion (a refresh) — the funnel snapshot carries
   * neither date, deliberately, because they are contract terms and the status
   * page reads them from the enrollment rather than from a cached copy.
   */
  readonly serviceStartDate: string | null;
  /** SWITCH only, and only on the signing tab. See above. */
  readonly rescissionDeadline: string | null;
}

export function SignCompletionPanel({
  statusToken,
  serviceStartDate,
  rescissionDeadline,
}: SignCompletionPanelProps): JSX.Element {
  const { t } = useTranslation();
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  // ⚠️ FOCUS, NOT `scrollIntoView`. Moving focus does BOTH jobs at once: it brings
  // the element into view for a sighted customer AND puts a screen reader's cursor
  // on the outcome. Scrolling alone would leave assistive technology where it was,
  // announcing nothing — which is the same silence this panel exists to end. The
  // heading carries `tabIndex={-1}` so it is programmatically focusable without
  // entering the tab order.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <Card variant="glass" padding="md" className="border-2 border-success/50" data-testid="sign-complete">
      <CardBody className="flex flex-col items-center gap-4 p-0 py-2 text-center">
        <div className="flex flex-col items-center gap-2" role="status" aria-live="polite">
          <h1
            ref={headingRef}
            tabIndex={-1}
            // A SOFT halo rather than a hard box. The ring must stay — moving focus
            // here is the whole mechanism, and WCAG 2.4.7 requires that a keyboard
            // user can see where it landed — but a tight 2px outline around a
            // heading reads as a text input, which is the wrong thing to look like
            // at the moment a contract completes.
            // Ring removed at product request — focus still lands here for AT;
            // the announcement, not a visible halo, is what the move-focus is for.
            className="font-display w-fit text-2xl font-bold tracking-tight text-foreground outline-none sm:text-3xl"
          >
            {t('shop.sign.confirm.title')}
          </h1>

          {serviceStartDate === null ? (
            // Re-derived completion (a refresh, or a second tab). We know the
            // enrollment is signed; we do not hold its dates, and inventing them
            // would be worse than sending the customer to the record that has them.
            <p className="text-sm text-foreground/80">{t('shop.sign.confirm.bodyNoDate')}</p>
          ) : (
            <p className="text-sm text-foreground/80">
              {t('shop.sign.confirm.body', { date: serviceStartDate })}
            </p>
          )}

          {rescissionDeadline === null ? null : (
            <p className="text-xs text-foreground/70">
              {t('shop.sign.confirm.rescission', { date: rescissionDeadline })}
            </p>
          )}
        </div>

        <div className="flex w-full flex-col items-center gap-3 border-t border-border pt-4">
          {/* A HEADING, not a styled paragraph: this is a distinct section of the
              confirmation and a screen-reader user navigating by heading has to be
              able to reach the download. */}
          <h2 className="text-sm font-semibold text-foreground">
            {t('shop.sign.confirm.nextSteps.heading')}
          </h2>

          {/* Download the signed contract (E-SF06) — proxied through the BFF via the
              anonymous status token, same as the CRM's quote download. A component,
              not an `<a download>`: the first download of a given contract renders
              it on demand and can run for minutes, which an anchor reports as
              neither progress nor a retryable failure.

              WRAPPED since the accept became asynchronous. Rendered directly, this
              offered a download for a contract the worker had not written yet — the
              request 404s and the customer is left with a spinner that never
              resolves, at the exact moment they want reassurance. The wrapper keeps
              the control VISIBLE (so the contract is evidently coming) and makes it
              usable only once the server says the document exists. */}
          <AgreementDownloadWhenReady statusToken={statusToken} />

          {/* ⚠️ THE DURABLE ADDRESS FOR THIS ENROLLMENT. Before this link existed,
              closing the tab was the end of the customer's access to their own
              contract from the storefront. */}
          <Link to={buildStatusPath(statusToken)}>
            <Button variant="primary" shape="pill" className="cta-sheen">
              {t('shop.sign.confirm.statusLink')}
            </Button>
          </Link>

          <p className="text-xs text-foreground/60">{t('shop.sign.confirm.emailNote')}</p>
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * The honest variant for a completion we can recognise but cannot LINK.
 *
 * Reachable when `GET /bff/funnel` reports `signed: true` with no status token —
 * an older session cached before the token crossed this boundary. It must never
 * fall back to re-rendering the sign surface: putting a live "Sign and enroll"
 * button in front of an executed contract is the defect, not the fallback.
 */
export function SignCompletionUnlinked(): JSX.Element {
  const { t } = useTranslation();
  return (
    <Alert variant="success" title={t('shop.sign.confirm.unlinkedTitle')} role="status">
      {t('shop.sign.confirm.unlinked')}
    </Alert>
  );
}
